declare global {
  interface Window {
    EXIF: {
      readFromBinaryFile: (data: ArrayBuffer) => Record<string, unknown> | null | false;
    };
    piexif: {
      ExifIFD: { DateTimeOriginal: number };
      dump: (exifObj: object) => string;
      insert: (exifBytes: string, dataUrl: string) => string;
    };
  }
}

const MAX_IMAGE_WIDTH = 1280;

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const base64Data = base64.split(',')[1] || base64;
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const resizeImage = (file: File, maxWidth: number = MAX_IMAGE_WIDTH): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const originalDataUrl = e.target?.result as string;

      // Read EXIF DateTimeOriginal from original file
      const exifData = window.EXIF?.readFromBinaryFile(base64ToArrayBuffer(originalDataUrl));
      const dateTimeOriginal = exifData && typeof exifData === 'object' ? (exifData.DateTimeOriginal || exifData.DateTime || exifData.DateTimeDigitized) as string | undefined : undefined;

      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        // If no resize needed, return original file
        if (width <= maxWidth) {
          resolve(file);
          return;
        }

        const ratio = maxWidth / width;
        width = maxWidth;
        height = height * ratio;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas conversion failed'));
              return;
            }

            // Re-insert DateTimeOriginal EXIF tag for JPEG files
            if (file.type === 'image/jpeg' && dateTimeOriginal && window.piexif) {
              const blobReader = new FileReader();
              blobReader.onload = (blobEvent) => {
                try {
                  let resizedDataUrl = blobEvent.target?.result as string;
                  const exifObj = { '0th': {}, Exif: {} as Record<number, string> };
                  exifObj.Exif[window.piexif.ExifIFD.DateTimeOriginal] = dateTimeOriginal;
                  const exifBytes = window.piexif.dump(exifObj);
                  resizedDataUrl = window.piexif.insert(exifBytes, resizedDataUrl);

                  // Convert data URL back to File
                  const byteString = atob(resizedDataUrl.split(',')[1]);
                  const ab = new ArrayBuffer(byteString.length);
                  const ia = new Uint8Array(ab);
                  for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                  }
                  const finalBlob = new Blob([ab], { type: file.type });
                  const resizedFile = new File([finalBlob], file.name, { type: file.type });
                  resolve(resizedFile);
                } catch {
                  const resizedFile = new File([blob], file.name, { type: file.type });
                  resolve(resizedFile);
                }
              };
              blobReader.onerror = () => {
                const resizedFile = new File([blob], file.name, { type: file.type });
                resolve(resizedFile);
              };
              blobReader.readAsDataURL(blob);
            } else {
              const resizedFile = new File([blob], file.name, { type: file.type });
              resolve(resizedFile);
            }
          },
          file.type,
          0.9
        );
      };

      img.onerror = () => reject(new Error('Image failed to load'));
      img.src = originalDataUrl;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
